import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEventManagementService } from '../services/eventManagementService';
import { actionLogKeys } from './actionLogKeys';
import type { ActionLogEntry, ActionLogResponse } from '../../../types/backend';

interface LinkedActionItemOptions {
  customerId: string;
  /** The first incidents page has loaded and no fetch of it is running. */
  firstPageSettled: boolean;
  /** The incidents data's version, so a page fetched after the strong get is known to be newer. */
  dataUpdatedAt: number;
  patchReports: (fn: (items: ActionLogEntry[]) => ActionLogEntry[]) => void;
  setError: (message: string | null) => void;
}

interface LinkedActionItem {
  /** The deep-linked entry while no incidents page holds it yet; listed beside the pages. */
  linkedEntry: ActionLogEntry | null;
  loadEntry: (opsitemId: string) => Promise<ActionLogEntry | null>;
  /** Applies a write to the linked entry; returning null drops it. */
  patchLinked: (fn: (entry: ActionLogEntry) => ActionLogEntry | null) => void;
}

/**
 * The action log's deep-linked entry (?actionItem=oi-...), fetched strongly by id so it resolves even when it has
 * scrolled past the loaded pages. It is listed at once, beside a first page that is still loading or has failed,
 * and moves into the incidents cache once a first page has settled.
 */
export function useLinkedActionItem({
  customerId, firstPageSettled, dataUpdatedAt, patchReports, setError,
}: LinkedActionItemOptions): LinkedActionItem {
  const eventService = useEventManagementService();
  const queryClient = useQueryClient();
  // A deep-linked entry that loaded before the first incidents page settled, or while it
  // failed: there is no page to put it in yet, and seeding one would clear the query's load
  // error. `pageAt` is the incidents data's version when it loaded.
  const [linked, setLinked] = useState<{ entry: ActionLogEntry; pageAt: number } | null>(null);
  const [linkedFor, setLinkedFor] = useState(customerId);
  // Adjusted while rendering rather than in an effect, so no frame lists one customer's entry under another.
  if (linkedFor !== customerId) {
    setLinkedFor(customerId);
    setLinked(null);
  }
  // The customer now shown, so a strong get that answers after a switch is dropped rather than listed.
  const currentCustomer = useRef(customerId);
  useLayoutEffect(() => { currentCustomer.current = customerId; }, [customerId]);

  // A stale copy in the reports cache is removed so the fresh strong get is the only one
  // listed. Null when the get failed or the customer changed while it was in flight.
  const loadEntry = useCallback(async (opsitemId: string): Promise<ActionLogEntry | null> => {
    try {
      const entry = await eventService.getActionLogItem(customerId, opsitemId);
      if (currentCustomer.current !== customerId) return null;
      const pageAt = queryClient.getQueryState(actionLogKeys.incidents(customerId))?.dataUpdatedAt ?? 0;
      setLinked({ entry, pageAt });
      patchReports((items) => items.filter((e) => e.opsitem_id !== entry.opsitem_id));
      return entry;
    } catch (err) {
      if (currentCustomer.current !== customerId) return null;
      setError(err instanceof Error ? err.message : 'Failed to load action log item');
      return null;
    }
  }, [customerId, eventService, queryClient, patchReports, setError]);

  // Once a first page has loaded (on first load or a retry), the cache takes over the linked
  // entry. A page fetched after the strong get that holds the entry has the fresher copy;
  // otherwise the entry goes into page 0 only, removing any stale copy from other pages, so
  // the merged table shows it without duplicating it. Waiting for the page matters: a first
  // page landing after an insert would replace the cache and drop the entry.
  useEffect(() => {
    if (!linked || !firstPageSettled) return;
    const { entry, pageAt } = linked;
    queryClient.setQueryData<InfiniteData<ActionLogResponse>>(
      actionLogKeys.incidents(customerId),
      (old) => {
        if (!old) return old;
        const pageIsNewer = dataUpdatedAt !== pageAt;
        if (pageIsNewer && old.pages.some((p) => p.items.some((e) => e.opsitem_id === entry.opsitem_id))) return old;
        return {
          ...old,
          pages: old.pages.map((p, i) => (i === 0
            ? { ...p, items: [entry, ...p.items.filter((e) => e.opsitem_id !== entry.opsitem_id)] }
            : { ...p, items: p.items.filter((e) => e.opsitem_id !== entry.opsitem_id) })),
        };
      },
    );
    patchReports((items) => items.filter((e) => e.opsitem_id !== entry.opsitem_id));
    setLinked(null);
  }, [linked, firstPageSettled, dataUpdatedAt, queryClient, customerId, patchReports]);

  // Stable, so closeItem and deleteItem, which list it as a dependency, keep their identity too.
  const patchLinked = useCallback((fn: (entry: ActionLogEntry) => ActionLogEntry | null) => {
    setLinked((current) => {
      if (!current) return current;
      const entry = fn(current.entry);
      return entry && { ...current, entry };
    });
  }, []);

  return { linkedEntry: linked?.entry ?? null, loadEntry, patchLinked };
}
